import {
  FormControl,
  FormHelperText,
  Paper,
  SxProps,
  Theme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import { PREDICAMENT_STATE_NO, PREDICAMENT_STATE_YES } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getCharacterCounter } from "core/model/utils/strings";
import {
  GKVMedicalFormData,
  PredicamentState,
  QueryProgressActive,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import DatePickerInputField, {
  ConnectedDateSelectorProps,
} from "ds_legacy/components/DatePickerInputField";
import { SubheadingFormLegend } from "ds_legacy/components/FormComponents/SubheadingFormLegend";
import RadioGroupV2, { RadioOptionV2 } from "ds_legacy/components/RadioGroupV2";
import RSButton from "ds_legacy/components/RSButton";
import ConnectedSelectInput, {
  ConnectedSelectInputProps,
} from "ds_legacy/components/SelectInput";
import {
  ConnectedTextarea,
  TextAreaProps,
} from "ds_legacy/components/TextArea";
import ConnectedTextInputField, {
  ConnectedTextInputProps,
  TextInputField,
} from "ds_legacy/components/TextInputField";
import ToggleInputField from "ds_legacy/components/ToggleInputField";
import {
  CUSTOM_BLACK,
  CUSTOM_WHITE,
  GREY_600,
  PRIMARY_DARK_COLOR,
  RADIO_WHITE_BACKGROUND,
  WHITE,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import {
  border,
  dp,
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import { SHADOW_DROP_SMALL } from "ds_legacy/materials/shadows";
import {
  Body,
  Caption,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_SIZE_24,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_REGULAR,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useVisibleCount } from "dsl/hooks";
import { FolderInputIcon, LockIcon, PlusIcon, PrinterIcon } from "lucide-react";
import {
  NUMBER_OF_OPS_FIELDS,
  OPERATION_INDEXES,
} from "onprem/mapping/mapFormToRehabRequest";
import { CSSProperties, ReactNode, useMemo } from "react";
import {
  FormElementProps,
  FormElementRenderProps,
  FormWatcher,
  isValid,
} from "react-forms-state";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { parsePerItemValidation } from "./utils";

export type Footnote = {
  description: (translations: Translations) => string;
  label: (translations: Translations) => string;
  superText: 1 | 2;
};

export const GAP = 3;

export const LARGE_INPUT_WIDTH = "35%";
export const LARGE_INPUT_MIN_WIDTH = dp(280);

export const SMALL_INPUT_WIDTH = `calc(17.5% - ${sizing(1.5)})`;
export const SMALL_INPUT_MIN_WIDTH = dp(130);

export const TEXT_AREA_MAX_CHARACTERS = 400;

const DEFAULT_VISIBLE_OPERATION_ROWS = 1;

export const patientSubheadingSx: CSSProperties = {
  padding: padding(GAP, 0, 1),
  fontSize: FONT_SIZE_18,
  fontWeight: FONT_WEIGHT_BOLD,
  margin: margin(0, 0, 0, 0.5),
  color: important(CUSTOM_BLACK),
};

export const StyledFormWrapper = styled(Paper)`
  box-shadow: ${SHADOW_DROP_SMALL} !important;
  border-radius: ${dp(6)};
  box-sizing: border-box;
  overflow: visible;
  width: 100%;
  max-width: ${dp(1366)};
  padding: ${padding(4, 15)};
  margin: ${sizing(5)} auto;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${sizing(1)};
  background-color: ${CUSTOM_WHITE};
  padding: ${padding(GAP)};
  box-sizing: border-box;
  overflow: visible;
`;

export const SectionRow = styled(HorizontalLayout)<{
  customPadding?: CSSProperties["padding"];
  fullWidth?: boolean;
}>`
  align-items: baseline;
  box-sizing: border-box;
  column-gap: ${sizing(GAP)};
  flex-wrap: wrap;
  overflow: visible;
  padding: ${({ customPadding }) => customPadding ?? padding(GAP, 0, 0)};
`;

const formatDate = (timestamp: number, locale: Locale) =>
  format(fromUnixTime(timestamp), "P", { locale });

const formatDateTime = (timestamp: number, time: number, locale: Locale) =>
  `${formatDate(timestamp, locale)} ${format(fromUnixTime(time), "HH:mm", {
    locale,
  })}`;

export function getDefaultVisibleOperationRows(
  formData: Partial<GKVMedicalFormData> | undefined,
) {
  if (!formData) return DEFAULT_VISIBLE_OPERATION_ROWS;

  let maxFilledIndex = 0;

  Object.values(OPERATION_INDEXES).forEach((opsIndex, i) => {
    const date =
      formData[`operation_${opsIndex}_date` as keyof GKVMedicalFormData];
    const ops =
      formData[`operation_${opsIndex}_ops` as keyof GKVMedicalFormData];
    const woundClosed =
      formData[
        `operation_${opsIndex}_wound_closed` as keyof GKVMedicalFormData
      ];

    if (date || ops || woundClosed) {
      // Record the **index+1** to count all rows up to this one
      maxFilledIndex = i + 1;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

export const getFootnoteAriaLabel = (
  footNote: Footnote,
  translations: Translations,
) => `${footNote.label(translations)} - ${footNote.description(translations)}`;

export const Section = ({
  children,
  title,
}: {
  children: ReactNode[] | ReactNode;
  title?: string;
}) => {
  return (
    <>
      {title ? (
        <PatientFormsSectionHeader>{title}</PatientFormsSectionHeader>
      ) : null}
      <SectionWrapper>{children}</SectionWrapper>
    </>
  );
};

export const FormSectionsWrapper = ({
  children,
  noBottomMargin,
}: {
  children: ReactNode[] | ReactNode;
  noBottomMargin?: boolean;
}) => {
  return (
    <VerticalLayout
      width="100%"
      margin={margin(1, 0, noBottomMargin ? 1 : 3)}
      rowGap={sizing(GAP)}
    >
      {children}
    </VerticalLayout>
  );
};

/**
 * @deprecated Accessibility issues:
 *   1. label nested inside fieldset and legend is not allowed
 *   2. label missing association with input
 */
export const WithLabel = ({
  bold,
  children,
  error,
  formLabelSx,
  label,
  required,
  wrapperSx,
}: {
  bold?: boolean;
  children: ReactNode;
  error: boolean;
  formLabelSx?: SxProps<Theme>;
  label: ReactNode | undefined;
  required?: boolean;
  wrapperSx: SxProps<Theme>;
}) => {
  return (
    <FormControl component="fieldset" error={error} sx={wrapperSx}>
      <SubheadingFormLegend
        sx={{
          margin: margin(0, 0, 1),
          fontSize: FONT_SIZE_14,
          fontWeight: bold ? FONT_WEIGHT_BOLD : undefined,
          ...formLabelSx,
        }}
      >
        <label>
          {label}
          {required ? " *" : ""}
        </label>
      </SubheadingFormLegend>
      {children}
    </FormControl>
  );
};

const LabelWrapper = ({
  bold,
  children,
  elementName,
  formLabelSx,
  label,
  required,
  wrapperSx,
}: {
  bold?: boolean;
  children: ReactNode;
  elementName: string;
  formLabelSx?: SxProps<Theme>;
  label: ReactNode | undefined;
  required?: boolean;
  wrapperSx: SxProps<Theme>;
}) => {
  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValidation }) => {
        return (
          <WithLabel
            bold={bold}
            label={label}
            error={!isValid(watchedValidation)}
            formLabelSx={formLabelSx}
            wrapperSx={wrapperSx}
            required={required}
          >
            {children}
          </WithLabel>
        );
      }}
    </FormWatcher>
  );
};

export const DatePickerWithLabel = (
  props: ConnectedDateSelectorProps & {
    bold?: boolean;
    elementName: string;
    wrapperSx?: SxProps;
  },
) => {
  const { bold, label, ...restProps } = props;
  return (
    <LabelWrapper
      bold={bold}
      wrapperSx={{
        width: SMALL_INPUT_WIDTH,
        minWidth: SMALL_INPUT_MIN_WIDTH,
        ...(restProps?.wrapperSx ?? {}),
      }}
      label={label}
      elementName={restProps.elementName}
      required={restProps.required}
    >
      <DatePickerInputField
        {...restProps}
        textInputVariant="outlined"
        inputSx={{
          minWidth: dp(165),
          "& .MuiInputBase-root": {
            background: WHITE,
          },
          width: "100%",
          ...(restProps?.inputSx ?? {}),
        }}
        formHelperTextProps={{ style: { margin: margin(1, 0, 0) } }}
        hasCustomValidation
      />
    </LabelWrapper>
  );
};

const CharacterLimit = ({
  characterLimit,
  characterLimitId,
  elementName,
}: {
  characterLimit?: number;
  characterLimitId: string;
  elementName: string;
}) => {
  const translations = useTranslations();

  if (!characterLimit) return null;

  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValidation, watchedValue }) => {
        if (watchedValidation !== true) return null;

        const characterLimitCounter = getCharacterCounter({
          characterLimit: characterLimit,
          value: watchedValue,
          translations,
        });

        return (
          <FormHelperText id={characterLimitId}>
            {characterLimitCounter}
          </FormHelperText>
        );
      }}
    </FormWatcher>
  );
};

export const TextInputWithLabel = ({
  bold,
  label,
  labelWrapperSx,
  inputProps = {},
  characterLimit,
  elementName,
  large,
  ...restProps
}: ConnectedTextInputProps & {
  bold?: boolean;
  characterLimit?: number;
  elementName: string;
  labelWrapperSx?: SxProps<Theme>;
  large?: boolean;
}) => {
  const characterLimitId = `${elementName}-character-limit`;
  return (
    <LabelWrapper
      bold={bold}
      wrapperSx={{
        width: large ? LARGE_INPUT_WIDTH : SMALL_INPUT_WIDTH,
        minWidth: large ? LARGE_INPUT_MIN_WIDTH : SMALL_INPUT_MIN_WIDTH,
        whiteSpace: "nowrap",
        ...labelWrapperSx,
      }}
      label={label}
      elementName={elementName}
      required={restProps.required}
    >
      <ConnectedTextInputField
        label={null}
        elementName={elementName}
        width="100%"
        marginOverride="0"
        variant="outlined"
        ariaDescribedBy={characterLimit ? characterLimitId : undefined}
        inputProps={{
          ...inputProps,
          maxLength: characterLimit,
        }}
        {...restProps}
        hasCustomValidation
      />
      <CharacterLimit
        characterLimit={characterLimit}
        characterLimitId={characterLimitId}
        elementName={elementName}
      />
    </LabelWrapper>
  );
};

export const TextAreaWithLabel = (
  props: TextAreaProps &
    FormElementProps & {
      bold?: boolean;
      elementName: string;
      formLabelSx?: SxProps<Theme>;
      wrapperSx?: SxProps<Theme>;
    },
) => {
  const { bold, formLabelSx, label, wrapperSx, ...restProps } = props;
  return (
    <LabelWrapper
      bold={bold}
      elementName={restProps.elementName}
      formLabelSx={formLabelSx}
      label={label}
      wrapperSx={{
        width: "100%",
        ...wrapperSx,
      }}
      required={props.required}
    >
      <ConnectedTextarea label={null} {...restProps} />
    </LabelWrapper>
  );
};

export const SelectWithLabel = (
  props: ConnectedSelectInputProps & {
    bold?: boolean;
    elementName: string;
    inputSx?: SxProps<Theme>;
    large?: boolean;
    wrapperSx: SxProps<Theme>;
  },
) => {
  const { bold, inputSx, label, wrapperSx, ...restProps } = props;
  return (
    <LabelWrapper
      bold={bold}
      elementName={restProps.elementName}
      label={label}
      wrapperSx={wrapperSx}
      required={restProps.required}
    >
      <ConnectedSelectInput
        label={null}
        {...restProps}
        size="small"
        variant="outlined"
        inputSx={{
          width: props.large ? LARGE_INPUT_WIDTH : SMALL_INPUT_WIDTH,
          minWidth: props.large ? LARGE_INPUT_MIN_WIDTH : SMALL_INPUT_MIN_WIDTH,
          background: WHITE,
          ...inputSx,
        }}
      />
    </LabelWrapper>
  );
};

export const ActivableToggle = ({
  componentIfFalse,
  componentIfTrue,
  elementName,
  label,
  sideMutation,
  wrapperStyle,
}: {
  componentIfFalse?: React.ReactNode;
  componentIfTrue?: React.ReactNode;
  elementName: string;
  label: string;
  sideMutation?:
    | ((newValue: boolean, mutateElement: AnyFunction) => void)
    | undefined;
  wrapperStyle?: CSSProperties;
}) => {
  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValue }) => {
        return (
          <VerticalLayout gap={sizing(2)} overflow="visible">
            <ToggleInputField
              elementName={elementName}
              label={label}
              noIndent
              wrapperStyle={{
                width: "100%",
                padding: padding(0),
                overflow: "visible",
                ...wrapperStyle,
              }}
              sideMutation={sideMutation}
            />
            {watchedValue ? componentIfTrue : componentIfFalse}
          </VerticalLayout>
        );
      }}
    </FormWatcher>
  );
};

export const FootnoteDescription = ({ footnote }: { footnote: Footnote }) => {
  const translations = useTranslations();

  return (
    <Caption aria-hidden margin={margin(0, 0, 1)}>
      <sup style={{ paddingRight: dp(2) }}>{footnote.superText}</sup>
      {footnote.description(translations)}
    </Caption>
  );
};

export const FootnoteLabel = ({ footnote }: { footnote: Footnote }) => {
  const translations = useTranslations();

  return (
    <>
      <span style={visuallyHidden}>
        {getFootnoteAriaLabel(footnote, translations)}
      </span>
      <span tabIndex={-1} aria-hidden style={{ position: "relative" }}>
        {footnote.label(translations)}
        <sup
          tabIndex={-1}
          aria-hidden
          style={{ paddingLeft: dp(2), position: "absolute", top: -8 }}
        >
          {footnote.superText}
        </sup>
      </span>
    </>
  );
};

export const YesNoRadioWithLabel = ({
  elementName,
  flatModel,
  formLabelSx,
  startWithNo,
  label,
  subForms = {},
  sideMutation,
  required,
}: {
  elementName: string;
  flatModel?: boolean;
  formLabelSx?: CSSProperties;
  label: string;
  required?: boolean;
  sideMutation?:
    | ((
        newValue: any,
        mutateElement: (value: any, elementName: string) => void,
      ) => void)
    | undefined;
  startWithNo?: boolean;
  subForms?: { [key: string]: React.ReactNode };
}) => {
  const translations = useTranslations();

  const defaultYesNoOptions: RadioOptionV2<PredicamentState>[] = [
    {
      value: PREDICAMENT_STATE_YES,
      label: translations.actions.yes,
      subForm: subForms[PREDICAMENT_STATE_YES],
    },
    {
      value: PREDICAMENT_STATE_NO,
      label: translations.actions.no,
      subForm: subForms[PREDICAMENT_STATE_NO],
    },
  ];

  return (
    <RadioGroupV2
      label={label}
      formControlLabelSx={{ paddingBottom: sizing(0.5), marginLeft: 0 }}
      formLabelSx={{
        padding: padding(1, 0),
        fontSize: FONT_SIZE_14,
        color: important(CUSTOM_BLACK),
        ...formLabelSx,
      }}
      elementName={elementName}
      flatModel={flatModel}
      options={
        startWithNo ? defaultYesNoOptions.reverse() : defaultYesNoOptions
      }
      radioSx={RADIO_WHITE_BACKGROUND}
      sideMutation={sideMutation}
      required={required}
    />
  );
};

export const DownloadSectionWrapper = ({
  body,
  lastUpdated,
  onClickPrint,
  onClickSendToHis,
  pdfName,
  progressOpenPdf,
  progressSendToHis,
  title,
}: {
  body: string;
  lastUpdated: number;
  onClickPrint: () => void;
  onClickSendToHis: () => void;
  pdfName: string;
  progressOpenPdf: QueryProgressActive;
  progressSendToHis: QueryProgressActive;
  title: string;
}) => {
  const translations = useTranslations();
  const { currentLocale: locale } = useDateLocale();
  const { hasTwoWaySync } = useHL7ImportStatusContext();

  return (
    <VerticalLayout
      data-testid="pdf-download-section"
      margin={margin(0, 0, 3)}
      width="100%"
      padding={padding(2, 3, 1)}
      style={{
        border: border({ width: 2, color: PRIMARY_DARK_COLOR }),
        borderRadius: sizing(1),
        boxSizing: "border-box",
      }}
    >
      <Title margin={margin(0, 0, 1.5)} as="h2">
        {title}
      </Title>
      <Body
        fontWeight={FONT_WEIGHT_REGULAR}
        margin="0"
        as="p"
        maxWidth="100%"
        fontSize={FONT_SIZE_14}
      >
        {body}
      </Body>
      <HorizontalLayout style={{ columnGap: sizing(3) }} aligned>
        <HorizontalLayout aligned>
          <Body
            margin="0"
            as="p"
            fontWeight={FONT_WEIGHT_BOLD}
            fontSize={FONT_SIZE_14}
          >
            {pdfName}
          </Body>
          <LockIcon
            size={16}
            style={{ margin: margin(0, 0, 0, 1), color: GREY_600 }}
          />
        </HorizontalLayout>
        <Body
          margin="0"
          as="p"
          color={GREY_600}
          fontWeight={FONT_WEIGHT_MEDIUM}
          fontSize={FONT_SIZE_14}
        >
          {translations.patientForms.transitionalCareForm.downloadSection.savedAt(
            {
              savedAt: formatDateTime(lastUpdated, lastUpdated, locale),
            },
          )}
        </Body>
        <RSButton
          color="primary"
          id="download"
          loading={progressOpenPdf}
          onClick={onClickPrint}
          style={{ width: "inherit" }}
          variant="text"
          LeftIcon={() => <PrinterIcon size={16} />}
        >
          {
            translations.patientForms.transitionalCareForm.downloadSection
              .printButton
          }
        </RSButton>
        {hasTwoWaySync ? (
          <RSButton
            id="send_to_his"
            loading={progressSendToHis}
            onClick={onClickSendToHis}
            variant="text"
            color="primary"
            LeftIcon={() => <FolderInputIcon size={FONT_SIZE_16} />}
          >
            {
              translations.patientForms.transitionalCareForm.downloadSection
                .hisButton
            }
          </RSButton>
        ) : null}
      </HorizontalLayout>
    </VerticalLayout>
  );
};

export const PatientFormsSectionHeader = ({
  children,
}: {
  children: string;
}) => {
  return (
    <Subheading as="h4" margin={margin(0)} style={{ fontSize: FONT_SIZE_24 }}>
      {children}
    </Subheading>
  );
};

export const PatientFormSubheading = ({
  children,
  style,
  withBottomPadding,
}: {
  children: string;
  style?: CSSProperties;
  withBottomPadding?: boolean;
}) => (
  <Subheading
    as="h5"
    style={{
      padding: padding(GAP, 0, withBottomPadding ? 1 : 0),
      fontSize: FONT_SIZE_18,
      fontWeight: FONT_WEIGHT_BOLD,
      margin: margin(0, 0, 0.5, 0.5),
      ...style,
    }}
  >
    {children}
  </Subheading>
);

function OpsFields({
  onChange,
  opsIndex,
  prefix,
  validation,
  value,
}: {
  onChange: (newValue: string[], statePath?: string) => void;
  opsIndex: number;
  prefix: string;
} & Pick<FormElementProps, "value" | "validation">) {
  const translations = useTranslations();

  const { initialOps, maxFilledIndex } = useMemo(() => {
    const ops: string[] = Array.isArray(value)
      ? [...value, ...Array(NUMBER_OF_OPS_FIELDS - value.length).fill("")]
      : Array(NUMBER_OF_OPS_FIELDS).fill("");

    const lastFilledIndex = ops.reduce((max, v, i) => {
      return v != null && v !== "" ? i + 1 : max;
    }, 0);

    return {
      initialOps: ops,
      maxFilledIndex: lastFilledIndex,
    };
  }, [value]);

  const {
    canIncrement,
    count: visibleCount,
    increment,
  } = useVisibleCount({
    initialCount: Math.max(maxFilledIndex, 1),
    maxCount: NUMBER_OF_OPS_FIELDS,
  });

  const handleOpChange = (index: number, newValue: string) => {
    const updated = [...initialOps];
    updated[index] = newValue;
    onChange(updated);
  };

  const { baseValidation, perItemErrors } = parsePerItemValidation(validation);

  return (
    <>
      {initialOps.map((opValue, index) => {
        if (index >= visibleCount) return null;

        const labelIndex = index + 1;
        const itemError = perItemErrors[index];

        return (
          <LabelWrapper
            key={index}
            bold
            wrapperSx={{
              width: LARGE_INPUT_MIN_WIDTH,
              minWidth: LARGE_INPUT_MIN_WIDTH,
              whiteSpace: "nowrap",
            }}
            label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${labelIndex}`}
            elementName={`${prefix}_ops_${labelIndex}`}
          >
            <TextInputField
              elementName={`${prefix}_ops_${labelIndex}`}
              hasCustomValidation
              label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${labelIndex}`}
              marginOverride={margin(0)}
              onChange={(v) => handleOpChange(index, v)}
              validation={itemError ? { infos: itemError } : baseValidation}
              value={opValue}
              variant="outlined"
            />
          </LabelWrapper>
        );
      })}
      <div className="flex justify-between">
        <RSButton
          buttonWrapperStyle={{ width: LARGE_INPUT_MIN_WIDTH }}
          color="primary"
          disabled={!canIncrement}
          id={`operation-${opsIndex}-add-ops-code-field`}
          LeftIcon={PlusIcon}
          loading="na"
          onClick={increment}
          style={{
            borderRadius: dp(8),
            margin: margin(0),
            width: LARGE_INPUT_MIN_WIDTH,
          }}
          trackEventProps={{
            id: `operation-${opsIndex}-add-ops-code-field`,
            visible_ops_code_fields: visibleCount,
          }}
          type="button"
          variant="outlined"
        >
          {translations.patientForms.medicalForm.sectionTwo.addOpsCode}
        </RSButton>
      </div>
    </>
  );
}

export function OperationsSubForm({
  initialVisibleOpsFields,
}: {
  initialVisibleOpsFields: number;
}) {
  const translations = useTranslations();
  const {
    canIncrement: canIncrementOpsFields,
    count: visibleOpsFields,
    increment: incrementOpsCount,
  } = useVisibleCount({
    initialCount: initialVisibleOpsFields,
    maxCount: Object.values(OPERATION_INDEXES).length,
  });

  return (
    <div className="flex w-full max-w-lg flex-col space-y-3 pl-7">
      <div className="flex w-full flex-col">
        {Object.values(OPERATION_INDEXES).map((opsIndex) => {
          const prefix = `operation_${opsIndex}`;
          return (
            <div
              key={opsIndex}
              className={clsx("flex w-full gap-4", {
                hidden: opsIndex > visibleOpsFields,
                "pt-8": opsIndex > 1,
              })}
            >
              <div>
                <DatePickerWithLabel
                  bold
                  elementName={`${prefix}_date`}
                  flatModel
                  label={
                    translations.patientForms.medicalForm.sectionTwo.surgeryDate
                  }
                />
              </div>
              <div className="flex w-full max-w-[316px] flex-col space-y-4">
                <FormElementRenderProps elementName={`${prefix}_ops`} flatModel>
                  {({ onChange, validation, value }) => (
                    <OpsFields
                      onChange={onChange}
                      opsIndex={opsIndex}
                      prefix={prefix}
                      validation={validation}
                      value={value}
                    />
                  )}
                </FormElementRenderProps>
                <YesNoRadioWithLabel
                  elementName={`${prefix}_wound_closed`}
                  flatModel
                  label={translations.patientForms.medicalForm.sectionTwo.wound}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between">
        <RSButton
          buttonWrapperStyle={{ width: "100%" }}
          color="primary"
          disabled={!canIncrementOpsFields}
          id="add-operation-field"
          loading="na"
          onClick={incrementOpsCount}
          style={{
            borderRadius: 8,
            margin: 0,
            width: "100%",
          }}
          trackEventProps={{
            id: "add-operation-field",
            visible_operation_fields: visibleOpsFields,
          }}
          type="button"
          variant="outlined"
          LeftIcon={PlusIcon}
        >
          {translations.patientForms.medicalForm.sectionTwo.addAnotherOperation}
        </RSButton>
      </div>
    </div>
  );
}
